import CachedIcon from "@mui/icons-material/Cached";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { Button, Grid2 } from "@mui/material";
import { ColDef, EditableCallbackParams } from "ag-grid-community";
import React from "react";
import { dxtToLocalServerTime } from "../../../../../../services/TimeService";
import { normalizeActionName } from "../../../hooks/useQueryCacheStats";
import { CacheConfiguration } from "../../../models/CacheConfiguration";
import { QueryCacheActionStats } from "../../../models/QueryCacheStats";

type Props = {
	setConfiguration: (configuration: CacheConfiguration) => void;
	setModalUpsert: (showModal: boolean) => void;
	setModalDelete: (showModal: boolean) => void;
	onClearCache: (configuration: CacheConfiguration) => void;
	showContextMenu: Function;
	cachedActions?: QueryCacheActionStats[];
};

const formatBytes = (bytes: number) => {
	if (!bytes) return "0 B";
	const units = ["B", "KB", "MB", "GB"];
	const exponent = Math.min(
		Math.floor(Math.log(bytes) / Math.log(1024)),
		units.length - 1,
	);
	const value = bytes / 1024 ** exponent;
	return `${value.toFixed(value >= 100 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

export const useTableData = ({
	setConfiguration,
	setModalUpsert,
	setModalDelete,
	onClearCache,
	showContextMenu,
	cachedActions,
}: Props) => {
	const columnDefs: ColDef[] = [
		{
			field: "ActionName",
			headerName: "Action",
			minWidth: 200,
			flex: 3,
		},
		{
			field: "TTLSeconds",
			headerName: "TTL (seconds)",
			minWidth: 85,
			maxWidth: 110,
			flex: 1,
		},
		{
			field: "IsActive",
			headerName: "Active",
			minWidth: 70,
			maxWidth: 80,
			flex: 1,
			valueFormatter: (params) => (params.value ? "Yes" : "No"),
		},
		{
			// Whether this configuration is actually doing anything. An action configured under a
			// name the application never calls stays empty forever, and there is no other sign of
			// it: the row looks perfectly fine.
			colId: "InCache",
			headerName: "In cache",
			minWidth: 175,
			flex: 2,
			sortable: false,
			valueGetter: (params) => {
				const configured = normalizeActionName(
					(params.data as CacheConfiguration)?.ActionName,
				);
				const cached = (cachedActions ?? []).find(
					(a) => normalizeActionName(a.actionName) === configured,
				);
				return cached
					? `${cached.rows.toLocaleString()} rows · ${formatBytes(cached.dataBytes)}`
					: "empty";
			},
			// The centering comes from defaultColDef.cellStyle, and a cellStyle on the column
			// replaces it instead of merging, so it has to be repeated here.
			cellStyle: (params) => ({
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				...(params.value === "empty"
					? { color: "#90a4ae", fontStyle: "italic" }
					: {}),
			}),
		},
		{
			field: "Comments",
			headerName: "Comments",
			minWidth: 120,
			flex: 2,
		},
		{
			field: "UpdatedBy",
			headerName: "Config Updated By",
			minWidth: 95,
			maxWidth: 120,
			flex: 1,
		},
		{
			// When the configuration itself was last changed, not when the cache was filled.
			// Stored in UTC, shown in the plant time zone like every other timestamp.
			// Wide enough for the whole "MM/dd/yyyy HH:mm" on one line.
			field: "UpdatedAt",
			headerName: "Config Updated At",
			minWidth: 165,
			flex: 2,
			valueGetter: (params) =>
				dxtToLocalServerTime(
					(params.data as CacheConfiguration)?.UpdatedAt,
					"MM/dd/yyyy HH:mm",
				),
		},
		{
			field: "Actions",
			cellRenderer: (params: EditableCallbackParams) => {
				// Always visible: these actions are the whole point of the row, and hiding them
				// until hover made them impossible to find.
				return (
					<Grid2
						container
						sx={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						{/* Four default MUI buttons are 64px each and overflowed the column, which is
						    what pushed the last icon against the edge. Narrowed, and with padding on
						    the right so nothing sits on the border. */}
						<Grid2
							size={{ xs: 12, md: 12 }}
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								pr: 1,
								"& .MuiButton-root": { minWidth: 40, px: 0.5 },
							}}
						>
							<Button
								title="Edit"
								onClick={() => {
									setConfiguration(params.data as CacheConfiguration);
									setModalUpsert(true);
								}}
							>
								<EditIcon color="primary" />
							</Button>
							<Button
								title="Clear cache"
								onClick={() => onClearCache(params.data as CacheConfiguration)}
							>
								<CachedIcon color="action" />
							</Button>
							<Button
								title="Delete"
								onClick={() => {
									setConfiguration(params.data as CacheConfiguration);
									setModalDelete(true);
								}}
							>
								<DeleteIcon color="secondary" />
							</Button>
							<Button
								onClick={(e) =>
									showContextMenu(e, params.data, "TableQueryCache")
								}
							>
								<FormatListBulletedSharpIcon
									style={{ height: "auto" }}
									color="action"
								/>
							</Button>
						</Grid2>
					</Grid2>
				);
			},
			headerName: " ",
			wrapText: false,
			autoHeight: false,
			wrapHeaderText: false,
			sortable: false,
			flex: 1,
			minWidth: 195,
			maxWidth: 215,
		},
	];

	return { columnDefs };
};
